import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { TranslationHook, TranslationResponse, TranslationState } from '../utils/types/UgcTranslations'
import { getCountryAndLocaleStrings } from '../utils/locales'

/* This hook is responsible for:
 *  1. Keeping track of what is displayed to the user (both the actual review/response texts,
 *     be they translated or not, as well as the prompt, such as "Translate to English")
 *  2. Handling the state of the translation (such as 'not required' or 'requested')
 *  3. Fetching the actual translation from an API Route (/translate)
 */
const useUgcTranslation = (originalTexts: string[], sourceLang: string): TranslationHook => {
  // Initialize a map structure so that (eventual) translation can be accessed with original string
  const originalTextMap = new Map<string, string>()

  originalTexts.forEach((text) => originalTextMap.set(text, text))

  const [texts, setTexts] = useState<Map<string, string>>(originalTextMap)
  // "cache" if translated back and forth
  const [translated, setTranslated] = useState<Map<string, string> | null>(null)
  const [translationState, setTranslationState] = useState<TranslationState>('not requested')
  const [userPrompt, setUserPrompt] = useState('')

  const { t } = useTranslation('products')
  const { locale } = useRouter()

  const destLang = getCountryAndLocaleStrings(locale).locale

  const revertPrompt = t('Show original')
  const translatePrompt = t('Translate review')

  /*
   * Unlike most features of the Review Center, this hook's logic and state depend
   * on the locale for more than just t() translations. This effect ensures everything
   * is updated whenever the user switches their language or country.
   */
  useEffect(() => {
    setTranslationState('not requested')
    setTexts(originalTextMap)
    setTranslated(null)
    setUserPrompt('')
  }, [locale])

  // No need for anything if user and content languages already match
  if (sourceLang === destLang && translationState !== 'not required') {
    setTranslationState('not required')
  }

  // If there is not a single text, no need for translations
  if (translationState !== 'not required') {
    let foundNonEmpty = false

    originalTexts.forEach((str) => {
      if (str.trim() !== '') {
        foundNonEmpty = true
      }
    })

    if (!foundNonEmpty) {
      setTranslationState('not required')
    }
  }

  if (userPrompt === '') {
    setUserPrompt(translatePrompt)
  }

  // Source language not in data, can't translate
  if (sourceLang === '' && translationState !== 'error') {
    setTranslationState('error')
  }

  /*
   * This effect runs every time translationState (or locale) changes and has three
   * responsibilities:
   * 1. If source and destination languages are the same, set status as 'not required'
   * 2. If they are not the same, but status is 'not required', sets that to 'not requested'. Note
   *    that this is only possible if the user has changed their language.
   * 3. If status is 'requested', fetches translations using our API route /translate
   */
  useEffect(() => {
    const fetchTranslations = async () => {
      const newTexts = new Map<string, string>()
      let errorDetected = false // Use a flag instead of modifying state in the Promise.all loop

      await Promise.all(originalTexts.map(async (originalText) => {
        // Fetch translation through API route, unless nothing to translate
        if (originalText.trim() === '') {
          newTexts.set(originalText, originalText)
        } else {
          try {
            const queryString = `?destLang=${destLang}&text=${encodeURI(originalText)}&sourceLang=${sourceLang}`
            const translationRes = await fetch(`/api/translate${queryString}`)
            const translationResBody: TranslationResponse = await translationRes.json()

            switch (translationResBody.message) {
              case 'Success':
                newTexts.set(originalText, translationResBody.result)
                break
              case 'Error':
                errorDetected = true
                console.error('Error in translation: ', translationResBody.result)
                break
              default:
                errorDetected = true
                console.error('Unexpected error in translation')
                break
            }
          } catch (e) {
            errorDetected = true
            console.error(`Unexpected error in translation: ${e}`)
          }
        }
      }))

      if (errorDetected) {
        setTranslationState('error')
        console.error('Aborting translation')

        return
      }

      setTranslated(newTexts)
      setTexts(newTexts)
      setUserPrompt(revertPrompt)
      setTranslationState('translated')
    }

    if (sourceLang === destLang) {
      setTranslationState('not required')
    } else if (translationState === 'not required') { // Only possible when user changed language
      setTranslationState('not requested')
      setUserPrompt(translatePrompt)
    }

    if (translationState === 'requested') {
      fetchTranslations()
    }
  }, [translationState, locale])

  // Called by the component that uses this hook when user clicks on translate/revert button
  const toggleTranslation = () => {
    switch (translationState) {
      case 'not requested':
        if (translated) {
          setTexts(translated)
          setTranslationState('translated')
          setUserPrompt(revertPrompt)
        } else {
          setTranslationState('requested')
        }
        break
      case 'translated':
        setTexts(originalTextMap)
        setTranslationState('not requested')
        setUserPrompt(translatePrompt)
        break
      default:
        break
    }
  }

  return {
    texts,
    translationState,
    userPrompt,
    toggleTranslation,
  }
}

export default useUgcTranslation
